/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PersistConfig } from "redux-persist";

import packageJson from "../../../../package.json";
import { RootState } from "../../../store/rootReducer";
import { error } from "../../../util/log";
import { storageDb } from "../../localforage";

import { RemoteConfigState } from "./types";

export const remoteConfigState: PersistConfig<RemoteConfigState> = {
  key: "remoteConfig",
  storage: storageDb,
  // serialize: false,
  version: 3,
};

export const remoteConfigInitialState: RemoteConfigState = {
  // App Management
  interval: 300,
  maintenance: false,
  maintenanceParams: {
    heading: "We are currently under going maintenance",
    message:
      "FarmlandsPro is currently undergoing scheduled maintenance and will be back shortly. We apologise for any inconvenience. If you need assistance, please contact <a href='mailto:ask@farmlands.co.nz'>ask@farmlands.co.nz</a>",
    excludedUsers: [],
  },
  quotePriceMin: 10000,
  quoteMaxFutureDays: 7,
  discountPercentageThreshold: 10,
  freeDeliveryThreshold: 500,
  version: packageJson.version,
  versionIsIgnored: true,
  authorisationTimeouts: {
    getAccounts: 30000,
    getAccountDetails: 30000,
    getUserDetails: 30000,
    getAtomicToken: 30000,
  },

  // Feature Flags
  atomicEnabled: true,
  atomicPromotionsEnabled: false,
  buyBreaksEnabled: false,
  costPriceEnabled: true,
  discountEnabled: true,
  drawdownFulfilmentEnabled: false,
  magnesiumPromoEnabled: false,
  quoteEnabled: true,
  activitiesEnabled: false,
  seedEnabled: true,
  traderDealsEnabled: false,
  traderDealsFilterValue: "trader",
  enableShopProductDetailsPage: false,
  enableShopCartPage: false,
  enableHome: false,
  // Pilots
  activitiesPilotUsers: {
    emails: [],
  },
  // In-App Urls
  privacyPolicyUrl: "",
  termsAndConditionsUrl: "",
  emailPreferencesCentreUrl: "",
  contactUrl: "https://www.farmlands.co.nz/contactus",
  directDebitRegisterUrl: "",
  accountAndCardFeesTermsUrl: "",
  farmlandsSecurityTipsUrl: "",
  farmlandsStoreLocatorUrl: "",
  farmlandsCardAppUrl: "",
  requestAdditionalCardUrl: "",
  statementsAndPaymentsUrl: "https://www.farmlands.co.nz/support/account/payments",
  latestVersion: packageJson.version,
  permissionsChecksEnabled: false,
};

const RemoteConfigSlice = createSlice({
  name: "remoteConfig",
  initialState: remoteConfigInitialState,
  reducers: {
    updateConfig(state: RemoteConfigState, { payload }: PayloadAction<RemoteConfigState>) {
      state = payload;
    },

    updateKey(
      state: RemoteConfigState,
      { payload }: PayloadAction<{ key: string; value: string | number | boolean | object }>
    ) {
      const { key, value } = payload;
      // Check if the key exists in the expected state
      if (Object.prototype.hasOwnProperty.call(remoteConfigInitialState, key)) {
        if (!state[key]) {
          state[key] = value;
          // Check if the type of the new value matches the type of the existing value
        } else if (typeof value === typeof state[key]) {
          state[key] = value;
        } else {
          // If types don't match, throw an error or handle it accordingly
          error(new Error(`Type mismatch for key '${key}'. Expected type: ${typeof state[key]}.`));
        }
      } else {
        // If the key doesn't exist, throw an error or handle it accordingly
        error(new Error(`Key '${key}' does not exist in state.`));
      }
    },
  },
});

const selectState = (state: RootState) => state;

export const selectRemoteConfig = createSelector(
  selectState,
  (state): RemoteConfigState => state.remoteConfig
);

export const selectShouldShowActivities = createSelector(
  selectState,
  (state) =>
    state.remoteConfig.activitiesEnabled ||
    state.remoteConfig.activitiesPilotUsers.emails
      .map((email) => email.toLowerCase())
      .includes(state.auth.userDetails?.primaryEmail?.toLowerCase() ?? "")
);

// Define the function with generics
export const selectConfigByKey = <K extends keyof RemoteConfigState>(key: K) => {
  return createSelector(selectState, (state) => {
    return state.remoteConfig[key] as RemoteConfigState[K];
  });
};

export default RemoteConfigSlice;
